/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useCallback, useMemo, useState, useEffect } from 'react'
import { PersonAdd, ManageAccounts } from '@mui/icons-material'
import { Box, Button, CircularProgress, Divider, Paper, Tab, Tabs, useTheme } from '@mui/material'

import { TAB_CONFIGS } from '../utils/Admin.utils'
import { FEATURE_FLAGS } from '@app/app-constants'
import { useAdminMetrics } from '../hooks/useAdminMetrics'
import { useFeatureFlags } from '@app/hooks/useFeatureFlags'
import { useFetchAdminUsers } from '../hooks/useFetchAdminUsers'
import { useResendAdminInvite } from '../hooks/useResendAdminInvite'

import AdminUsersTable from '../components/Admin.UsersTable'
import { AdminTopUsersList } from '../components/Admin.TopUsersList'
import { AdminInviteDrawer } from '../components/Admin.InviteDrawer'
import { CenteredBox } from '@app/components/ui/CenteredBox/CenteredBox'
import { AdminTotalUsersCard } from '../components/Admin.TotalUsersCard'
import { AdminApprovedDomainCard } from '../components/Admin.ApprovedDomainCard'
import { AdminConfirmedUsersCard } from '../components/Admin.ConfirmedUsersCard'
import { AdminPendingInvitesCard } from '../components/Admin.PendingInvitesCard'
import { ManageUserRolesDrawer } from '../components/Admin.ManageUserRolesDrawer'
import {
  containerWrapperCard,
  dashboardWrapper,
  headerButtonsContainerStyle,
  headerSection,
  infoContainer,
  metricsSection,
  tabContent,
  tabsSection,
} from '../styles/Admin.styles'
import { useFetchCustomUserRoles } from '../hooks/useCustomUserRoles'

export const NewAdminPage = () => {
  const theme = useTheme()
  const { useFeatureFlag } = useFeatureFlags()
  const [activeTab, setActiveTab] = useState(0)
  const resendInviteMutation = useResendAdminInvite()
  const { data: users = [], isLoading: isLoadingUsers } = useFetchAdminUsers()
  const { data: customUserRoles, isLoading: customRolesLoading } = useFetchCustomUserRoles()

  const canManageCustomUserRoles = useFeatureFlag(FEATURE_FLAGS.CUSTOM_USER_ROLES)

  const [emailsToInvite, setEmailsToInvite] = useState<string>('')
  const [opensInviteDrawer, setOpensInviteDrawer] = useState(false)
  const [openManageRolesDrawer, setOpenManageRolesDrawer] = useState(false)
  const [resendingEmails, setResendingEmails] = useState<Set<string>>(new Set())

  const metrics = useAdminMetrics(users)
  const filteredUsers = useMemo(() => TAB_CONFIGS[activeTab].filterFn(users), [activeTab, users])

  const handleResendInvite = useCallback(
    (email: string) => {
      if (!email) return

      setResendingEmails((prev) => new Set(prev).add(email))

      resendInviteMutation.mutate(email, {
        onSettled: () => {
          setResendingEmails((prev) => {
            const next = new Set(prev)
            next.delete(email)
            return next
          })
        },
      })
    },
    [resendInviteMutation],
  )
  const handleChangeTab = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  useEffect(() => {}, [])

  if (isLoadingUsers) {
    return (
      <Box css={{ position: 'relative', height: 'calc(100vh - 300px)', width: '100%' }}>
        <CenteredBox>
          <CircularProgress />
        </CenteredBox>
      </Box>
    )
  }

  return (
    <Box css={dashboardWrapper}>
      <Box css={headerSection}>
        <Box
          css={css`
            display: none;
            ${theme.breakpoints.up('md')} {
              display: block;
            }
          `}
        ></Box>
        <Box css={headerButtonsContainerStyle}>
          {canManageCustomUserRoles ? (
            <Button
              variant="contained"
              size="small"
              onClick={() => setOpenManageRolesDrawer(true)}
              startIcon={<ManageAccounts />}
            >
              Manage Roles
            </Button>
          ) : null}
          <Button
            variant="contained"
            size="small"
            onClick={() => setOpensInviteDrawer(true)}
            startIcon={<PersonAdd />}
          >
            Invite Users
          </Button>
        </Box>
      </Box>

      <Box css={metricsSection}>
        <AdminTotalUsersCard count={metrics.total} title="Total Users" />
        <AdminConfirmedUsersCard count={metrics.confirmed.length} title="Confirmed Users" />
        <AdminPendingInvitesCard count={metrics.pending.length} title="Pending Invites" />
        <AdminApprovedDomainCard count={3} title="Approved Domains" />
      </Box>

      <Box css={tabsSection} component={Paper}>
        <Tabs
          value={activeTab}
          onChange={handleChangeTab}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="All Users" id="tab-0" aria-controls="tabpanel-0" />
          <Tab label="Pending Invites" id="tab-1" aria-controls="tabpanel-1" />
        </Tabs>

        <Divider css={{ 'border-color': '#d9d9d9' }} />

        <Box css={tabContent} role="tabpanel" id="tabpanel-0" aria-labelledby="tab-0">
          <AdminUsersTable
            users={filteredUsers}
            onResendInvite={handleResendInvite}
            resendingEmails={resendingEmails}
            customRoles={customUserRoles}
          />
        </Box>
      </Box>

      <Box css={containerWrapperCard({ theme })}>
        <Box css={infoContainer({ theme })} component={Paper}>
          <AdminTopUsersList users={users} />
        </Box>
      </Box>

      <AdminInviteDrawer
        opensInviteDrawer={opensInviteDrawer}
        setOpensInviteDrawer={setOpensInviteDrawer}
        emailsToInvite={emailsToInvite}
        setEmailsToInvite={setEmailsToInvite}
        customRoles={customUserRoles}
        customRolesLoading={customRolesLoading}
      />
      <ManageUserRolesDrawer
        opensUserRolesDrawer={openManageRolesDrawer}
        setOpensUserRolesDrawer={setOpenManageRolesDrawer}
        customRoles={customUserRoles}
        customRolesLoading={customRolesLoading}
      />
    </Box>
  )
}

export default NewAdminPage
